import React, { useEffect, useMemo, useState } from 'react';
import { useP } from '../../i18n/useP';
import PageShell from '../../components/PageShell';
import TrajectoireSketch from '../../components/TrajectoireSketch';
import { listNews } from '../../api/news';
import { listHrPeople, listHrContracts, listPayrollMovements } from '../../api/hr';
import { logicalType } from './planningEngine';
import PeopleImportModal, { Ico } from './PeopleImportModal';
import './HrPage.css';
import './PeopleHome.css';

/* Accueil People — proto v6 d'Olivier (2026-09-01) : l'accueil s'ouvre sur CE
   QUI BOUGE, pas sur un menu. Une colonne : bande d'état + tuile Résumé, puis
   « Ce qui a bougé » (contrats du dernier mois clôturé) face à « À confirmer »
   (mouvements simulés), puis les tuiles d'action en format compact, puis
   « Quoi de neuf ». Ardoise seul accent ; le vert ne dit qu'une hausse d'EPT ;
   aucun terracotta ici. */

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const MOIS_AB = ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.',
  'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];

const nEpt = (v) => v.toLocaleString('fr-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nChf = (v) => Math.round(Math.abs(v)).toLocaleString('de-CH').replace(/,/g, "'");
const shortName = (p_) => `${(p_.first_name || '')[0] || ''}. ${p_.last_name || ''}`.trim();
const fullName = (p_) => `${p_.first_name || ''} ${p_.last_name || ''}`.trim();
const initials = (name) => name.split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase();
const fmtDate = (iso) => { const [y, m, d] = String(iso).slice(0, 10).split('-'); return `${d}.${m}.${y}`; };

/* Effet signé : vert UNIQUEMENT sur une hausse d'EPT. */
const Delta = ({ text, up }) => <span className={`ph-lg__delta${up ? ' is-up' : ''}`}>{text}</span>;

function Tile({ icon, title, sub, tone = '', onClick }) {
  return (
    <button type="button" className={`ph-tile${tone ? ` ph-tile--${tone}` : ''}`} onClick={onClick}>
      <Ico k={icon} />
      <span className="ph-tile__txt">
        <b>{title}</b>
        <span>{sub}</span>
      </span>
      <span className="ph-tile__arrow" aria-hidden>→</span>
    </button>
  );
}

export default function PeopleHomePage({ activeTenant, addToast, year, embedded = false, userName = null,
                                         onAddPerson, onStory, onMove, onGoMoves, onPlan, onTrajectory,
                                         onGoContracts }) {
  const p = useP();
  const [screen, setScreen] = useState(null);
  const [news, setNews] = useState([]);
  const [people, setPeople] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [movements, setMovements] = useState([]);
  const tid = activeTenant?.id;

  useEffect(() => {
    if (!tid) return;
    listNews('people', 5).then(r => setNews(r.data || [])).catch(() => setNews([]));
    listHrPeople(tid).then(r => setPeople(r.data || [])).catch(() => setPeople([]));
    listHrContracts(tid).then(r => setContracts(r.data || [])).catch(() => setContracts([]));
    listPayrollMovements(tid).then(r => setMovements(r.data || [])).catch(() => setMovements([]));
  }, [tid]);

  /* ── Mois clôturé / mois ouvert — la même notion que partout
     (tenants.last_closed_period). Sans clôture, la bande se tait. ── */
  const lcp = activeTenant?.last_closed_period || null;
  const [cy, cm] = lcp ? String(lcp).split('-').map(Number) : [null, null];   // cm : 1-12
  const oy = cm === 12 ? cy + 1 : cy;
  const om = cm === 12 ? 1 : cm + 1;

  const byPerson = useMemo(() => {
    const m = {};
    for (const c of contracts) (m[c.person_id] || (m[c.person_id] = [])).push(c);
    for (const k of Object.keys(m)) m[k].sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
    return m;
  }, [contracts]);
  const personById = useMemo(() => Object.fromEntries(people.map(x => [x.id, x])), [people]);

  /* ── Ce qui a bougé : les contrats du mois clôturé ── */
  const contractEvents = useMemo(() => {
    if (!cy) return [];
    const inClosed = (iso) => {
      if (!iso) return false;
      const [y, m] = String(iso).slice(0, 10).split('-').map(Number);
      return y === cy && m === cm;
    };
    const ev = [];
    for (const pid of Object.keys(byPerson)) {
      const person = personById[pid];
      if (!person) continue;
      const list = byPerson[pid];
      list.forEach((c, i) => {
        if (inClosed(c.start_date)) {
          const prev = i > 0 ? list[i - 1] : null;
          const pct = Math.round(Number(c.activity_rate || 0) * 100);
          if (!prev) {
            ev.push({ date: c.start_date, who: fullName(person), what: p('arrivée, {{pct}} %', { pct }),
                      delta: `+${nEpt(Number(c.activity_rate || 0))} EPT`, up: true });
            return;
          }
          const dRate = Number(c.activity_rate || 0) - Number(prev.activity_rate || 0);
          const dSal = Number(c.monthly_salary || 0) - Number(prev.monthly_salary || 0);
          if (Math.round(dRate * 100) !== 0) {
            ev.push({ date: c.start_date, who: fullName(person),
                      what: `${Math.round(Number(prev.activity_rate || 0) * 100)} % → ${pct} %`,
                      delta: `${dRate > 0 ? '+' : '−'}${nEpt(Math.abs(dRate))} EPT`, up: dRate > 0 });
          } else if (Math.round(dSal) !== 0) {
            ev.push({ date: c.start_date, who: fullName(person),
                      what: dSal > 0 ? p('augmentation') : p('baisse de salaire'),
                      delta: `${dSal > 0 ? '+' : '−'}${nChf(dSal)} CHF/mois` });
          }
        }
        const isLast = i === list.length - 1;
        if (isLast && inClosed(c.end_date)) {
          ev.push({ date: c.end_date, who: fullName(person), what: p('départ'),
                    delta: `−${nEpt(Number(c.activity_rate || 0))} EPT` });
        }
      });
    }
    ev.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return ev;
  }, [byPerson, personById, cy, cm, p]);

  /* ── À confirmer : les mouvements du kanban, à partir du mois ouvert ── */
  const pending = useMemo(() => {
    if (!oy) return { rows: [], total: 0, inOpen: 0 };
    const openKey = `${oy}-${String(om).padStart(2, '0')}`;
    const list = movements.filter(mv => String(mv.start_month || '').slice(0, 7) >= openKey);
    const inOpen = list.filter(mv => String(mv.start_month || '').slice(0, 7) === openKey).length;
    // Regroupement par mois de début, puis par nature ; salaire et position
    // ouverte gardent leur ligne (elles portent un montant ou un EPT).
    const months = {};
    for (const mv of list) {
      const key = String(mv.start_month).slice(0, 7);
      (months[key] || (months[key] = [])).push(mv);
    }
    const rows = [];
    for (const key of Object.keys(months).sort()) {
      const [y, m] = key.split('-').map(Number);
      const isOpen = key === openKey;
      const label = MOIS_AB[m - 1] + (y !== year ? ` ${y}` : '');
      const mvs = months[key];
      const singles = mvs.filter(mv => mv.movement_type === 'salary_change'
        || (mv.movement_type === 'hire' && !mv.person_id));
      for (const mv of singles) {
        const person = mv.person_id ? personById[mv.person_id] : null;
        if (mv.movement_type === 'salary_change') {
          const amt = Number(mv.monthly_amount || 0);
          rows.push({ month: label, isOpen, who: person ? fullName(person) : (mv.label || '—'), what: p('salaire'),
                      delta: `${amt >= 0 ? '+' : '−'}${nChf(amt)} CHF/mois` });
        } else {
          rows.push({ month: label, isOpen, who: mv.label || p('position ouverte'), what: p('position ouverte'),
                      delta: `+${nEpt(Number(mv.fte || 0))} EPT`, up: true });
        }
      }
      // le reste, par nature : taux, fin de contrat, réduction générale
      const rest = mvs.filter(mv => !singles.includes(mv));
      const byKind = {};
      for (const mv of rest) {
        const k = logicalType(mv);
        (byKind[k] || (byKind[k] = [])).push(mv);
      }
      const KIND_LBL = {
        rate_change: p('taux'), exit: p('fin de contrat'),
        cut: p('réduction générale'), hire: p('position ouverte'),
      };
      for (const k of Object.keys(byKind)) {
        const ms = byKind[k];
        const names = ms.map(mv => mv.person_id && personById[mv.person_id]
          ? shortName(personById[mv.person_id]) : (mv.label || '—'));
        rows.push({ month: label, isOpen,
                    who: names.join(', '), what: KIND_LBL[k] || k,
                    delta: ms.length > 1 ? p('{{n}} mouvements', { n: ms.length }) : p('1 mouvement') });
      }
    }
    return { rows, total: list.length, inOpen };
  }, [movements, personById, oy, om, year, p]);

  if (!activeTenant) {
    return (
      <PageShell embedded={embedded} framed={!embedded} title={p('Accueil')}>
        <div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div>
      </PageShell>
    );
  }

  const moisClos = cy ? MOIS[cm - 1] : null;
  const shown = contractEvents.slice(0, 8);
  const pendShown = pending.rows.slice(0, 5);

  return (
    <PageShell embedded={embedded} framed={!embedded} title={p('Accueil')}>
      <div className="ph-home">
        {/* ── Résumé + Ce qui a bougé (colonne gauche) / À confirmer ──── */}
        {/* La bande d'état a vécu une matinée (2026-09-01) : « mettre à jour
            les contrats » n'est pas un geste quotidien, elle pressait sans
            raison. Le Résumé prend sa place, à la largeur de la card
            du dessous. */}
        {/* Les gestes OUVRENT la page (2026-09-01) : quatre tuiles sur une
            ligne, les liens du réel dessous, avant le récit et les
            nouveautés. */}
        <div className="ph-tiles ph-tiles--four">
          <Tile icon="add" title={p('Ajouter un collaborateur')} sub={p("Une personne, à l'écran")}
                onClick={() => onAddPerson && onAddPerson()} />
          <Tile icon="move" tone="sim" title={p('Poser un mouvement')}
                sub={p('Arrivée, départ, changement de taux')}
                onClick={() => onMove && onMove()} />
          <Tile icon="plan" tone="sim" title={p('Planifier la masse salariale')}
                sub={p("Le prévisionnel de l'exercice")}
                onClick={() => onPlan && onPlan()} />
          <Tile icon="route" tone="sim" title={p('Tracer la trajectoire')}
                sub={p("Au-delà de l'exercice, pluriannuel")}
                onClick={() => onTrajectory && onTrajectory()} />
        </div>

        {/* Les autres mises à jour du réel, en liens discrets sous les
            gestes : elles se font quelques fois l'an, pas chaque jour. */}
        <div className="ph-updates">
          <button type="button" onClick={() => setScreen('collaborateurs')}>
            <Ico k="users" />{p('Importer des collaborateurs')}
          </button>
          <button type="button" onClick={() => setScreen('extras')}>
            <Ico k="bonus" />{p('Compléments (Extras)')}
          </button>
          <button type="button" onClick={() => setScreen('frais')}>
            <Ico k="frais" />{p('Frais')}
          </button>
          <button type="button" onClick={() => setScreen('indemnites')}>
            <Ico k="indem" />{p('Indemnités reçues')}
          </button>
        </div>

        {/* ── Héros du récit (gauche) · nouveautés (droite) ───────────── */}
        <div className="ph-top">
          <button type="button" className="ph-hero" onClick={() => onStory && onStory()}>
            {/* Le péristyle en filigrane : la MARQUE elle-même (composant
                Logo, jamais redessinée), en ardoise très claire. */}
            <span className="ph-hero__mark" aria-hidden>
              {/* « La trajectoire se trace » (proto, variante 1) : la courbe
                  remplace le disque de lecture. */}
              <TrajectoireSketch />
            </span>
            <span className="ph-hero__eyebrow">{p('Présentation exécutive')}</span>
            <span className="ph-hero__title">{p('Les chiffres en récit')}</span>
            <span className="ph-hero__sub">{p('Effectifs, mouvements, recul et perspectives')}</span>
            {/* Le signe de lecture OUVRE le bouton : un seul geste, plus de
                rond flottant sur le filigrane. */}
            <span className="ph-hero__cta">
              <svg className="ph-hero__cta-play" viewBox="0 0 24 24" aria-hidden><path d="M9 7.5v9l7.5-4.5z" /></svg>
              {p('Voir la présentation')}
            </span>
          </button>
          {/* Quoi de neuf — le coin haut droit ────────────────────────────────────────────── */}
          {news.length > 0 && (
            <section className="ph-card ph-news">
              <p className="ph-home__lead">{p('Nouveautés')}</p>
              <ul>
                {news.map((n) => (
                  <li key={n.id}>
                    <span className="ph-news__date">{fmtDate(n.news_date)}</span>
                    <span className="ph-news__txt">{n.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>




        {/* ── Ce qui a bougé / À confirmer : côte à côte, même hauteur ── */}
        <div className="ph-move">

          <section className="ph-card">
            <div className="ph-card__head">
              <span className="ph-home__lead">{p('Changements dans les contrats')}{moisClos && <> · {moisClos}</>}</span>
              <button type="button" className="ph-card__more" onClick={() => onGoContracts && onGoContracts()}>{p('Voir tout')}</button>
            </div>
            {shown.length === 0 ? (
              <p className="ph-lg__empty">{moisClos
                ? p('Aucun changement de contrat en {{month}}.', { month: moisClos })
                : p('Aucun mois clôturé pour cet exercice.')}</p>
            ) : (
              <ul className="ph-lg">
                {shown.map((e, i) => (
                  <li key={i}>
                    <span className="ph-lg__ava" aria-hidden>{initials(e.who)}</span>
                    <span><span className="ph-lg__who">{e.who}</span> <span className="ph-lg__what">{e.what}</span>
                      <span className="ph-lg__date">{fmtDate(e.date)}</span></span>
                    <Delta text={e.delta} up={e.up} />
                  </li>
                ))}
                {contractEvents.length > 8 && (
                  <li className="ph-lg__moremark">{p('+ {{n}} autres', { n: contractEvents.length - 8 })}</li>
                )}
              </ul>
            )}
          </section>

          <section className="ph-card">
            <div className="ph-card__head">
              <span className="ph-home__lead">{p('Simulations & mutations')} · {pending.total}</span>
              <button type="button" className="ph-card__more" onClick={() => (onGoMoves ? onGoMoves() : (onMove && onMove()))}>{p('Voir tout')}</button>
            </div>
            {pendShown.length === 0 ? (
              <p className="ph-lg__empty">{p('Aucun mouvement simulé.')}</p>
            ) : (
              <ul className="ph-lg">
                {pendShown.map((r, i) => (
                  <li key={i}>
                    <span className={`ph-lg__d${r.isOpen ? ' is-now' : ''}`}>{r.month}</span>
                    <span>
                      <span className="ph-lg__who">{r.who}</span> <span className="ph-lg__what">{r.what}</span>
                      {r.isOpen && <span className="ph-lg__tag">{p('à confirmer')}</span>}
                    </span>
                    <Delta text={r.delta} up={r.up} />
                  </li>
                ))}
                {pending.rows.length > 5 && (
                  <li className="ph-lg__moremark">{p('+ {{n}} autres', { n: pending.rows.length - 5 })}</li>
                )}
              </ul>
            )}
          </section>
        </div>


      </div>
      {screen && (
        <PeopleImportModal
          tenantId={activeTenant.id}
          year={year}
          addToast={addToast}
          initialScreen={screen}
          onAddPerson={onAddPerson}
          onClose={() => setScreen(null)}
        />
      )}
    </PageShell>
  );
}
